import { logger } from '@/lib/logger';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
// No Phosphor imports needed

export default function GetDemoModal({ isOpen, onClose }) {
    const [formStatus, setFormStatus] = useState('idle');

    if (!isOpen) return null;

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('submitting');

        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            await fetch('https://formsubmit.co/ajax/oblique@eleventh.tech', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    _subject: `New Demo Request: ${data.name} from ${data.companyName}`,
                    _template: 'table',
                    "Full Name": data.name,
                    "Phone Number": data.phone,
                    "Email Address": data.email,
                    "Company Name": data.companyName,
                    "Company Size": data.companySize,
                    "Reason for Use": data.reason
                })
            });
            setFormStatus('success');
            e.target.reset();
            setTimeout(() => {
                setFormStatus('idle');
                onClose();
            }, 3000);
        } catch (error) {
            logger.error('Submission error', error);
            setFormStatus('error');
        }
    };

    return (
        <AnimatePresence>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                    onClick={onClose}
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    style={{
                        background: 'var(--bg-surface)',
                        borderRadius: '24px',
                        width: '100%',
                        maxWidth: '550px',
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'hidden',
                        border: '1px solid var(--border)',
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div style={{ padding: '24px 32px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div>
                            <h2 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>Get a Demo</h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>See how Oblique protects your pipeline revenue.</p>
                        </div>
                        <button onClick={onClose} style={{ background: 'var(--bg-muted)', border: 'none', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)', cursor: 'pointer', transition: 'background 0.2s' }} className="hover:bg-[var(--border)]">
                            <ObliqueIcon name="close" size={18} />
                        </button>
                    </div>

                    <div style={{ padding: '32px', overflowY: 'auto' }}>
                        <form onSubmit={handleFormSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>Full Name</label>
                                    <input name="name" type="text" required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>Phone No.</label>
                                    <input name="phone" type="tel" required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>Work Email</label>
                                    <input name="email" type="email" required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>Company Name</label>
                                    <input name="companyName" type="text" required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>Company Size</label>
                                <select name="companySize" required defaultValue="" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }}>
                                    <option value="" disabled>Select Team Size</option>
                                    <option value="1-50">1-50 Employees</option>
                                    <option value="51-200">51-200 Employees</option>
                                    <option value="201-500">201-500 Employees</option>
                                    <option value="500+">500+ Employees</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>Reason for Use</label>
                                <textarea name="reason" required rows={3} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)', resize: 'vertical' }} />
                            </div>

                            <button type="submit" disabled={formStatus === 'submitting'} style={{ width: '100%', padding: '16px', background: 'var(--accent)', color: '#fff', fontSize: '14px', fontWeight: '500', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', opacity: formStatus === 'submitting' ? 0.7 : 1 }} className="hover:opacity-90">
                                {formStatus === 'submitting' ? 'Submitting request...' : formStatus === 'success' ? 'Demo Request Sent!' : 'Request Demo'}
                            </button>
                            {formStatus === 'success' && <div style={{ textAlign: 'center', marginTop: '12px', color: 'var(--success)', fontSize: '14px' }}>We have received your demo request and will reach out soon.</div>}
                        </form>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
